/**
 * "Wichtige Warnungen" — the full-width warning panel of the Co-Living
 * dashboard (Slice 2, restyled in Slice 18).
 *
 * Reine Präsentation über `GET /api/admin/co-living/upcoming` → `warnings`.
 * Der Server entscheidet, WAS eine Warnung ist und WIE DRINGEND sie ist; diese
 * Komponente entscheidet nur, wie das aussieht. Konkret wird hier nichts
 * hergeleitet: kein `days_until`, kein Nachfolger, kein Status, keine Coverage,
 * keine Frist. Jede angezeigte Zahl und jedes Datum steht so auf dem Draht.
 *
 * `type` steuert ausschliesslich Label, Punktfarbe und Textanordnung — nie, ob
 * etwas eine Warnung ist. Ein unbekannter `type` wird deshalb bewusst trotzdem
 * gerendert (Message + Datum) statt stillschweigend verschluckt: eine Warnung,
 * die der Server schickt und die UI verschweigt, ist genau der Fehlermodus, den
 * dieser Slice beseitigt.
 *
 * Datumsformat über `formatDateOnly`: für `YYYY-MM-DD` ein reiner String-Split,
 * keine `Date`-Konstruktion. Damit bleibt 2026-10-31 auch 31.10.2026 — kein
 * Zeitzonen-Versatz, kein +1 (CLAUDE.md Regel 51, docs/TIMEZONE_ARCHITECTURE.md).
 *
 * SLICE 18 — WAS SICH GEÄNDERT HAT UND WAS NICHT. Die Karte ist jetzt eine
 * Panel-Fläche über die volle Breite, mit den Warnungen als Kacheln
 * nebeneinander statt als Liste untereinander, und sie trägt nur noch Tokens
 * (`bg-card`, `border-border`, `text-muted-foreground`) statt fester Hex-Werte
 * — dadurch funktioniert Light Mode, weil das Theme es sagt, nicht weil
 * `index.css` jede Farbe zurückmalt. Die Semantik ist unverändert: dieselbe
 * Quelle, dieselbe Reihenfolge des Contracts, keine neue Priorisierung, keine
 * neue Schwelle, und weiterhin drei streng getrennte Zustände.
 *
 * KEIN "Alle Warnungen anzeigen". Die Referenzgrafik zeigt den Link; in Vantio
 * gibt es kein Ziel dafür. Ein Element, das aussieht wie eine Navigation und
 * keine ist, wäre eine erfundene Funktion (Slice-18 §31).
 */
import React from "react";
import { AlertTriangle, CalendarClock, CircleAlert } from "lucide-react";

import { formatDateOnly } from "../../../utils/datetime";

/** Label, Ton und Icon pro Warnungstyp. Präsentation, keine Businessregel. */
const WARNING_PRESENTATION = {
  upcoming_termination: {
    label: "Kündigung ohne Anschluss",
    icon: CircleAlert,
    tone: "bg-destructive/10 text-destructive",
  },
  unit_lease_start_uncovered: {
    label: "Lease-Start ohne volle Belegung",
    icon: CalendarClock,
    tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  },
};

const FALLBACK_PRESENTATION = {
  label: "Beobachtung",
  icon: AlertTriangle,
  tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
};

/** Anzeigename einer Einheit. Reine Fallback-Kette, keine Herleitung. */
function unitLabel(warning) {
  return warning.unit_short_id || warning.unit_title || warning.unit_id || "Unit";
}

/** Grammatik, keine Rechnung: `days_until` kommt unverändert vom Server. */
function daysLabel(daysUntil) {
  if (daysUntil === null || daysUntil === undefined) return null;
  return daysUntil === 1 ? "1 Tag" : `${daysUntil} Tage`;
}

/** Stabiler Key ohne Index-Abhängigkeit, wo der Contract eine ID liefert. */
function warningKey(warning, index) {
  return [
    warning.type ?? "warning",
    warning.tenancy_id ?? warning.unit_id ?? "",
    warning.relevant_date ?? "",
    index,
  ].join("-");
}

function WarningMeta({ children }) {
  const parts = React.Children.toArray(children).filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <p className="m-0 mt-1.5 text-[10px] leading-[1.5] text-muted-foreground">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <span className="mx-[5px] opacity-50">·</span> : null}
          {part}
        </React.Fragment>
      ))}
    </p>
  );
}

function WarningItem({ warning }) {
  const presentation = WARNING_PRESENTATION[warning.type] ?? FALLBACK_PRESENTATION;
  const Icon = presentation.icon;
  const days = daysLabel(warning.days_until);
  const date = warning.relevant_date ? formatDateOnly(warning.relevant_date) : null;

  // Kopfzeile: bei einer Kündigung ist die Person die Identität des Falls, bei
  // allem anderen die Einheit. Reine Textanordnung pro `type`.
  const headline =
    warning.type === "upcoming_termination"
      ? warning.tenant_display_name || unitLabel(warning)
      : unitLabel(warning);

  return (
    <li className="flex items-start gap-2.5 rounded-[10px] border border-border bg-card p-3">
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] ${presentation.tone}`}
        aria-hidden
      >
        <Icon className="h-[15px] w-[15px]" strokeWidth={2} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="m-0 min-w-0 text-[12px] font-semibold leading-snug text-foreground">
            {headline}
          </p>
          {days ? (
            <span className="shrink-0 rounded-[5px] bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {days}
            </span>
          ) : null}
        </div>

        {/* Der Typ steht als Text da, nicht nur als Farbe. */}
        <p className="m-0 mt-0.5 text-[10px] font-medium text-muted-foreground">
          {presentation.label}
        </p>

        {warning.message ? (
          <p className="m-0 mt-1 text-[11px] leading-[1.5] text-muted-foreground">
            {warning.message}
          </p>
        ) : null}

        {warning.type === "upcoming_termination" ? (
          <WarningMeta>
            {date}
            {unitLabel(warning)}
            {warning.room_name || null}
          </WarningMeta>
        ) : warning.type === "unit_lease_start_uncovered" ? (
          <WarningMeta>
            {date}
            {`${warning.uncovered_rooms} von ${warning.active_rooms} Zimmern unbelegt`}
          </WarningMeta>
        ) : (
          <WarningMeta>{date}</WarningMeta>
        )}
      </div>
    </li>
  );
}

/**
 * @param {object|null} snapshot  Antwort von `fetchAdminCoLivingUpcoming`, oder
 *                                null solange noch geladen wird bzw. nach einem Fehler.
 * @param {string}      error     Fehlermeldung; leer = kein Fehler.
 */
export default function CoLivingWarningsCard({ snapshot, error }) {
  const warnings = Array.isArray(snapshot?.warnings) ? snapshot.warnings : [];
  const isLoading = snapshot === null && !error;

  return (
    <section
      aria-label="Wichtige Warnungen"
      className="rounded-[10px] border border-destructive/20 bg-destructive/[0.04] p-4"
    >
      <div className="flex items-start gap-2.5">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] bg-destructive/10 text-destructive"
          aria-hidden
        >
          <AlertTriangle className="h-[15px] w-[15px]" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="m-0 text-[13px] font-semibold text-destructive">Wichtige Warnungen</h2>
          <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
            Diese Themen erfordern deine Aufmerksamkeit.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="m-0 mt-3 text-[12px] text-muted-foreground">Wird geladen…</p>
      ) : error ? (
        // Ein technischer Fehler darf NIE als "keine Warnungen" erscheinen —
        // sonst liest sich ein totes Backend als ruhige Lage.
        <p className="m-0 mt-3 text-[12px] font-medium text-destructive" role="alert">
          {error}
        </p>
      ) : warnings.length === 0 ? (
        <p className="m-0 mt-3 text-[12px] text-muted-foreground">
          <span className="mr-1.5 text-emerald-600 dark:text-emerald-400" aria-hidden>
            ✓
          </span>
          Keine aktiven Warnungen
        </p>
      ) : (
        <ul className="m-0 mt-3 grid list-none grid-cols-1 gap-2.5 p-0 sm:grid-cols-2 xl:grid-cols-3">
          {warnings.map((warning, index) => (
            <WarningItem key={warningKey(warning, index)} warning={warning} />
          ))}
        </ul>
      )}
    </section>
  );
}
